import { pluralize } from "../../../utils";
import {
  HealthTargetsResponse,
  SaveHealthTargetsPayload,
} from "../../../services";
import {
  HealthTargetFieldConfig,
  HealthTargetFormValues,
  HealthTargetMetric,
  NumericRuleConfig,
} from "./types";

const hasAtMostDecimals = (value: number, places: number) =>
  Number(value.toFixed(places)) === value;

const numericRule =
  ({
    label,
    min,
    max,
    maxDecimals,
    integer = false,
    integerMessage = `${label} target must be a whole number`,
    minMessage = `${label} target must be at least ${min}`,
    maxMessage = `${label} target must not be greater than ${max}`,
  }: NumericRuleConfig) =>
  (value: string): true | string => {
    const trimmed = value.trim();
    if (trimmed === "") {
      return true;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return `${label} target must be a number`;
    }
    if (integer && !Number.isInteger(parsed)) {
      return integerMessage;
    }
    if (maxDecimals !== undefined && !hasAtMostDecimals(parsed, maxDecimals)) {
      return `${label} target must have at most ${maxDecimals} decimal ${pluralize(maxDecimals, "place")}`;
    }
    if (parsed < min) {
      return minMessage;
    }
    if (parsed > max) {
      return maxMessage;
    }
    return true;
  };

export const HEALTH_TARGET_FIELDS: HealthTargetFieldConfig[] = [
  {
    metric: "savingsRate",
    name: "savingsRateMinPct",
    label: "Savings rate",
    comparator: "at least",
    unit: "%",
    spacedUnit: false,
    inputMode: "decimal",
    rules: {
      validate: numericRule({
        label: "Savings rate",
        min: 1,
        max: 100,
        maxDecimals: 1,
      }),
    },
  },
  {
    metric: "runway",
    name: "runwayMinMonths",
    label: "Invest. runway",
    comparator: "at least",
    unit: "months",
    spacedUnit: true,
    inputMode: "numeric",
    rules: {
      validate: numericRule({
        label: "Runway",
        min: 1,
        max: 600,
        integer: true,
        integerMessage: "Runway target must be a whole number of months",
        minMessage: "Runway target must be at least 1 month",
        maxMessage: "Runway target must not be greater than 600 months",
      }),
    },
  },
  {
    metric: "debtToAsset",
    name: "debtToAssetMax",
    label: "Debt-to-asset",
    comparator: "at most",
    unit: "×",
    spacedUnit: false,
    inputMode: "decimal",
    rules: {
      validate: numericRule({
        label: "Debt-to-asset",
        min: 0,
        max: 10,
        maxDecimals: 2,
      }),
    },
  },
  {
    metric: "burnRate",
    name: "burnRateMaxPct",
    label: "Burn rate",
    comparator: "below",
    unit: "%",
    spacedUnit: false,
    inputMode: "decimal",
    rules: {
      validate: numericRule({
        label: "Burn rate",
        min: 1,
        max: 100,
        maxDecimals: 1,
      }),
    },
  },
];

export const isHealthTargetMetric = (
  value: string | null | undefined,
): value is HealthTargetMetric =>
  HEALTH_TARGET_FIELDS.some((field) => field.metric === value);

const numberToText = (value: number | null) =>
  value === null ? "" : String(value);

export const toFormValues = (
  response: HealthTargetsResponse,
): HealthTargetFormValues => ({
  savingsRateMinPct: numberToText(response.savingsRateMinPct),
  runwayMinMonths: numberToText(response.runwayMinMonths),
  debtToAssetMax: numberToText(response.debtToAssetMax),
  burnRateMaxPct: numberToText(response.burnRateMaxPct),
});

const textToNumber = (value: string) =>
  value.trim() === "" ? null : Number(value);

export const toPayload = (
  values: HealthTargetFormValues,
): SaveHealthTargetsPayload => ({
  savingsRateMinPct: textToNumber(values.savingsRateMinPct),
  runwayMinMonths: textToNumber(values.runwayMinMonths),
  debtToAssetMax: textToNumber(values.debtToAssetMax),
  burnRateMaxPct: textToNumber(values.burnRateMaxPct),
});
